import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { useNoshStore, FoodItem } from '../lib/store';
import { daysUntilExpiry, getExpiryColor, getCategoryColor, cn } from '../lib/utils';
import { getUnitValues } from '../lib/measurement-units';
import { DatabaseService } from '../lib/database';
import { Plus, Search, Trash2, Edit, Grid, List, X, Filter, ChevronDown, Check, ArrowDown, ArrowUp, ArrowDownAZ, ArrowUpAZ, Calendar, ShoppingCart, RotateCcw, ScanLine, Sparkles, Clock, AlertTriangle, Image as ImageIcon, FolderOpen, Camera, ChevronDown as ChevronDownIcon, Edit2, MoreHorizontal, Calendar as CalendarIcon, Star, AlertCircle, Package2 } from 'lucide-react';
// import { ThemeToggle } from '../components/ui/theme-toggle';
import { useGlobalImageScanner } from '../lib/global-image-scan';
// UsageLimitModal now handled globally in App.tsx

import { useMobileSwipeBack } from '../components/ui/mobile-swipe-back';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';

type FilterOption = {
  id: string;
  label: string;
  value: string;
};

type SortOption = {
  id: string;
  label: string;
  key: keyof FoodItem | 'expiry' | 'alphabetical' | 'quantity';
  direction: 'asc' | 'desc';
};

// Add a mapping for location to border color (move outside component)
const LOCATION_BORDER_MAP: Record<string, string> = {
  fridge: 'border-l-4 border-blue-400',
  pantry: 'border-l-4 border-yellow-500',
  freezer: 'border-l-4 border-cyan-400',
  default: 'border-l-4 border-gray-300',
};

// Get background color based on expiration date
const getExpiryBackgroundColor = (days: number, location: string): string => {
  // Special handling for freezer items - they get a light blue/cyan background
  if (location === 'freezer') {
    return 'bg-cyan-50 dark:bg-cyan-900/10';
  }
  
  if (days < 0) {
    // Expired - Red background
    return 'bg-red-100 dark:bg-red-900/20';
  } else if (days === 0) {
    // Expiring today - Red background
    return 'bg-red-100 dark:bg-red-900/20';
  } else if (days === 1) {
    // 1 day - Subtle orange (toned down)
    return 'bg-orange-100 dark:bg-orange-900/15';
  } else if (days === 2) {
    // 2 days - Lighter orange (same base as 1 day, but lighter)
    return 'bg-orange-50 dark:bg-orange-900/7';
  } else if (days === 3) {
    // 3 days - Very light orange (same base as 1 day, but very light)
    return 'bg-orange-50 dark:bg-orange-900/4';
  } else if (days === 4) {
    // 4 days - Light yellow
    return 'bg-yellow-50 dark:bg-yellow-900/10';
  } else if (days === 5) {
    // 5 days - Very light yellow-green
    return 'bg-lime-50 dark:bg-lime-900/10';
  } else if (days === 6) {
    // 6 days - Light green
    return 'bg-green-50 dark:bg-green-900/10';
  } else {
    // 7+ days - Same fresh green for all
    return 'bg-green-50 dark:bg-green-900/10';
  }
};

// Add your actual categories here (should match your DB)
const FOOD_CATEGORIES = [
  'Meat', 'Seafood', 'Dairy', 'Vegetables', 'Fruits', 'Grains', 'Nuts & Seeds', 'Other'
];

// Helper function to get expiry color classes that handles freezer location
const getExpiryColorWithLocation = (days: number, location: string): string => {
  if (location === 'freezer') {
    return 'bg-cyan-100 text-cyan-700 dark:text-cyan-300 border border-cyan-200';
  }
  
  if (days < 0) return 'bg-destructive/20 text-destructive border border-destructive/30';
  if (days < 3) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/20';
  if (days < 7) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
  return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
};

// Helper function to get expiry display text
const getExpiryDisplayText = (days: number, location: string): string => {
  if (location === 'freezer') {
    return '❄️ Frozen';
  }
  
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  return `${days} days`;
};

// Helper function to get expiry display text for desktop (more verbose)
const getExpiryDisplayTextDesktop = (days: number, location: string): string => {
  if (location === 'freezer') {
    return '❄️ Frozen';
  }
  
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires Today';
  return `Expires in ${days} days`;
};

// Item Details Modal Component
const ItemDetailsModal = ({ 
  item, 
  isOpen, 
  onClose 
}: { 
  item: FoodItem | null; 
  isOpen: boolean; 
  onClose: () => void; 
}) => {
  const { updateItem, removeItem, user } = useNoshStore();
  
  // Get unit options based on user's measurement system preference
  const unitOptions = getUnitValues(user.measurementSystem || 'metric');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedLocation, setSelectedLocation] = useState<'fridge' | 'pantry' | 'freezer'>(item?.location || 'fridge');
  const [editedName, setEditedName] = useState(item?.name || '');
  const [editedQuantity, setEditedQuantity] = useState<number | string>(item?.quantity || 1);
  const [editedUnit, setEditedUnit] = useState(item?.unit || 'pieces');
  const [editedExpiryDate, setEditedExpiryDate] = useState(item?.expiryDate || '');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCalendar) {
        const target = event.target as Element;
        if (!target.closest('.calendar-container')) {
          setShowCalendar(false);
        }
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalendar]);

  useEffect(() => {
    if (item) {
      setSelectedLocation(item.location);
      setEditedName(item.name);
      setEditedQuantity(item.quantity);
      setEditedUnit(item.unit);
      setEditedExpiryDate(item.expiryDate);
    }
  }, [item]);

  if (!isOpen || !item) return null;

  const days = daysUntilExpiry(editedExpiryDate);
  const modalIconBg = getExpiryBackgroundColor(days, item.location);

  const handleSaveItem = async () => {
    const updates: Partial<FoodItem> = {};
    
    if (editedName !== item.name) updates.name = editedName;
    
    // Convert editedQuantity to number for comparison and assignment
    const finalQuantity = typeof editedQuantity === 'string' ? parseFloat(editedQuantity) || 1 : editedQuantity;
    if (finalQuantity !== item.quantity) updates.quantity = finalQuantity;
    if (editedUnit !== item.unit) updates.unit = editedUnit;
    if (editedExpiryDate !== item.expiryDate) updates.expiryDate = editedExpiryDate;
    
    // Handle location change with expiry date recalculation
    if (selectedLocation !== item.location) {
      updates.location = selectedLocation;
      
      // If moving FROM freezer to another location, recalculate expiry date
      if (item.location === 'freezer' && selectedLocation !== 'freezer') {
        const today = new Date();
        let newExpiryDate: Date;
        
        // Estimate shelf life based on food category and new location
        const category = item.category.toLowerCase();
        let shelfLifeDays = 7; // Default 1 week
        
        if (selectedLocation === 'fridge') {
          // Fridge shelf life estimates
          if (category.includes('meat') || category.includes('seafood') || category.includes('fish')) {
            shelfLifeDays = 3; // 3 days for meat/seafood in fridge
          } else if (category.includes('dairy')) {
            shelfLifeDays = 7; // 1 week for dairy
          } else if (category.includes('vegetable') || category.includes('fruit')) {
            shelfLifeDays = 5; // 5 days for produce
          } else {
            shelfLifeDays = 7; // 1 week default for fridge
          }
        } else if (selectedLocation === 'pantry') {
          // Pantry shelf life estimates
          if (category.includes('grain') || category.includes('nuts') || category.includes('legume')) {
            shelfLifeDays = 30; // 1 month for dry goods
          } else if (category.includes('condiment') || category.includes('sweetener')) {
            shelfLifeDays = 90; // 3 months for condiments
          } else if (category.includes('meat') || category.includes('seafood') || category.includes('dairy')) {
            shelfLifeDays = 1; // 1 day - these shouldn't be in pantry long
          } else {
            shelfLifeDays = 14; // 2 weeks default for pantry
          }
        }
        
        newExpiryDate = new Date(today.getTime() + (shelfLifeDays * 24 * 60 * 60 * 1000));
        updates.expiryDate = newExpiryDate.toISOString().split('T')[0];
        setEditedExpiryDate(updates.expiryDate);
        
        console.log(`📦 Moving ${item.name} from freezer to ${selectedLocation}, new expiry: ${updates.expiryDate} (${shelfLifeDays} days)`);
      }
    }

    if (Object.keys(updates).length > 0) {
      await updateItem(item.id, updates);
    }
    onClose();
  };

  const handleDeleteItem = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteItem = async () => {
    if (item) {
      await removeItem(item.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const cancelDeleteItem = () => {
    setShowDeleteConfirm(false);
  };

  const handleDateChange = (date: string) => {
    setEditedExpiryDate(date);
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  // Remove hardcoded units - now using dynamic unitOptions from above

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25, ease: "linear" }}
        onClick={onClose}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(8px)',
          zIndex: 9998,
          margin: 0, 
          padding: 0 
        }}
      />

      {/* Modal Content */}
      <div 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          margin: 0, 
          padding: isMobile ? '0 1rem' : '0' 
        }}
        onClick={onClose}
      >
        {isMobile ? (
          // Mobile: iOS-styled modal
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              duration: 0.3,
              ease: "easeOut"
            }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Edit Item
                  </h3>
                  {/* Delete Button */}
                  <button
                    onClick={handleDeleteItem}
                    className="h-9 w-9 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center transition-all duration-200 active:scale-95"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-all duration-200 active:scale-95"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 pb-6 space-y-6">
              {/* Item Info - Same Row */}
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 ${modalIconBg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                  <span className="text-3xl">{item.emoji}</span>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Item Name
                  </label>
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium"
                    placeholder="Enter item name"
                  />
                </div>
              </div>

              {/* Quantity and Unit */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={editedQuantity || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        // Allow empty value during typing
                        setEditedQuantity('');
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue > 0) {
                          setEditedQuantity(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure valid value when input loses focus
                      const numQuantity = typeof editedQuantity === 'string' ? parseFloat(editedQuantity) : editedQuantity;
                      if (!editedQuantity || isNaN(numQuantity) || numQuantity <= 0) {
                        setEditedQuantity(1);
                      }
                    }}
                    min="1"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-medium text-center"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={editedUnit}
                    onChange={(e) => setEditedUnit(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200"
                    placeholder="e.g., g, kg, pieces, ml"
                  />
                </div>
              </div>

              {/* Expiry Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Expiry Date
                </label>
                <div className="flex items-center gap-3">
                  {/* Status Tag */}
                  <div className={`inline-block rounded-full px-3 py-2 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)} flex-shrink-0`}>
                    {getExpiryDisplayText(days, item.location)}
                  </div>
                  
                  {/* Direct Date Input */}
                  <div className="relative flex-1">
                    <input
                      type="date"
                      value={formatDateForInput(editedExpiryDate)}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-200"
                    />
                    <Calendar size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Location
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['fridge', 'pantry', 'freezer'] as const).map((location) => (
                    <button
                      key={location}
                      onClick={() => setSelectedLocation(location)}
                      className={`p-4 rounded-2xl border-2 transition-all duration-200 active:scale-95 ${
                        selectedLocation === location 
                          ? 'border-primary bg-primary/10 shadow-sm' 
                          : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">
                          {location === 'fridge' && '🧊'}
                          {location === 'pantry' && '🏠'}
                          {location === 'freezer' && '❄️'}
                        </div>
                        <div className={`text-xs font-semibold capitalize ${
                          selectedLocation === location 
                            ? 'text-primary' 
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {location}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <button
                  onClick={handleSaveItem}
                  className="w-full flex items-center justify-center px-4 py-4 rounded-2xl bg-primary text-white font-semibold transition-all duration-200 hover:bg-primary/90 active:scale-95 shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          // Desktop: Standard modal
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ 
              duration: 0.2,
              ease: "easeOut"
            }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Edit Item
                  </h3>
                  {/* Delete Button */}
                  <button
                    onClick={handleDeleteItem}
                    className="h-9 w-9 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center transition-all duration-200 active:scale-95"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-all duration-200 active:scale-95"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Item Icon and Basic Info */}
              <div className="flex items-center">
                <div className={`w-24 h-24 ${modalIconBg} rounded-2xl flex items-center justify-center mr-6`}>
                  <span className="text-5xl">{item.emoji}</span>
                </div>
                <div className="flex-1">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Item Name
                    </label>
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Enter item name"
                    />
                  </div>
                </div>
              </div>

              {/* Quantity and Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={editedQuantity || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        // Allow empty value during typing
                        setEditedQuantity('');
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue > 0) {
                          setEditedQuantity(numValue);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure valid value when input loses focus
                      const numQuantity = typeof editedQuantity === 'string' ? parseFloat(editedQuantity) : editedQuantity;
                      if (!editedQuantity || isNaN(numQuantity) || numQuantity <= 0) {
                        setEditedQuantity(1);
                      }
                    }}
                    min="1"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={editedUnit}
                    onChange={(e) => setEditedUnit(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., g, kg, pieces, ml"
                  />
                </div>
              </div>

              {/* Expiry Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Expiry Date
                </label>
                <div className="flex items-center gap-3">
                  {/* Expires Status Tag */}
                  <div className={`inline-block rounded-full px-4 py-2 text-sm font-medium ${getExpiryColorWithLocation(days, item.location)}`}>
                    {getExpiryDisplayTextDesktop(days, item.location)}
                  </div>
                  
                  {/* Date Picker */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => {
                        const dateInput = document.getElementById(`date-input-${item.id}`) as HTMLInputElement;
                        if (dateInput) {
                          if (dateInput.showPicker) {
                            dateInput.showPicker();
                          } else {
                            dateInput.focus();
                          }
                        }
                      }}
                      className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 flex items-center justify-center hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
                    >
                      <Calendar size={16} className="mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                        {new Date(editedExpiryDate).toLocaleDateString()}
                      </span>
                    </button>
                    
                    {/* Hidden date input */}
                    <input
                      id={`date-input-${item.id}`}
                      type="date"
                      value={formatDateForInput(editedExpiryDate)}
                      onChange={(e) => handleDateChange(e.target.value)}
                      className="absolute opacity-0 pointer-events-none"
                      tabIndex={-1}
                    />
                  </div>
                </div>
              </div>

              {/* Move Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                  Location
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['fridge', 'pantry', 'freezer'] as const).map((location) => (
                    <button
                      key={location}
                      onClick={() => setSelectedLocation(location)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        selectedLocation === location 
                          ? 'border-primary bg-primary/10 text-primary' 
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-3xl mb-2">
                          {location === 'fridge' && '🧊'}
                          {location === 'pantry' && '🏠'}
                          {location === 'freezer' && '❄️'}
                        </div>
                        <div className="text-sm font-medium capitalize">
                          {location}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleSaveItem}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "linear" }}
            onClick={cancelDeleteItem}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(8px)',
              zIndex: 10000,
              margin: 0, 
              padding: 0 
            }}
          />

          {/* Confirmation Modal */}
          <div 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              margin: 0, 
              padding: '0 1rem' 
            }}
            onClick={cancelDeleteItem}
          >
            {isMobile ? (
              // Mobile: iOS-styled confirmation
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ 
                  duration: 0.2,
                  ease: "easeOut"
                }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Content */}
                <div className="text-center p-6 pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Delete {item.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                    Are you sure you want to delete this item? This action cannot be undone.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2">
                    <button
                      onClick={cancelDeleteItem}
                      className="px-6 py-4 text-blue-600 dark:text-blue-400 font-medium border-r border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteItem}
                      className="px-6 py-4 text-red-600 dark:text-red-400 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              // Desktop: Modern web-styled confirmation
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ 
                  duration: 0.3,
                  ease: "easeOut"
                }}
                className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 w-full max-w-md mx-auto overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with Icon */}
                <div className="text-center p-8 pb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-2xl flex items-center justify-center">
                    <Trash2 size={24} className="text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-gray-900 dark:text-gray-100 mb-3">
                    Delete {item.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    Are you sure you want to delete this item from your inventory? This action cannot be undone and all data will be permanently removed.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="px-8 pb-8 flex gap-3">
                  <Button
                    onClick={cancelDeleteItem}
                    variant="outline"
                    className="flex-1 h-11 rounded-xl font-medium border-2 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    Keep Item
                  </Button>
                  <Button
                    onClick={confirmDeleteItem}
                    className="flex-1 h-11 rounded-xl font-medium bg-red-600 hover:bg-red-700 text-white border-2 border-red-600 hover:border-red-700"
                  >
                    Delete Forever
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}
    </>,
    document.body
  );
};

// Add this before the main Inventory component
const ScanButtonWithTooltip = React.memo(({ 
  triggerFileInput, 
  scanLoading 
}: { 
  triggerFileInput: () => void; 
  scanLoading: boolean; 
}) => {
  const [showScanTooltip, setShowScanTooltip] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Update button position when tooltip shows
  useEffect(() => {
    if (showScanTooltip && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          setButtonRect(buttonRef.current.getBoundingClientRect());
        }
      };
      
      updatePosition();
      // Also update on scroll/resize to maintain correct position
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showScanTooltip]);
  
  return (
    <>
      <Button 
        ref={buttonRef}
        onClick={triggerFileInput}
        disabled={scanLoading}
        onMouseEnter={() => setShowScanTooltip(true)}
        onMouseLeave={() => setShowScanTooltip(false)}
        className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-violet-600 to-purple-700 hover:from-purple-600 hover:via-violet-700 hover:to-purple-800 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group ai-scan-button"
      >
        {/* Animated background shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        
        {/* Content */}
        <div className="relative flex items-center gap-2">
          {scanLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="font-medium">Scanning...</span>
            </>
          ) : (
            <>
              <div className="relative">
                <ScanLine size={18} className="relative z-10" />
                {/* Sparkle effects */}
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full ai-sparkle-1" />
                <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-pink-300 rounded-full ai-sparkle-2" />
              </div>
              <span className="font-medium">Scan with AI</span>
              <Sparkles size={14} className="animate-pulse text-yellow-200" />
            </>
          )}
        </div>
        
        {/* Glow effect */}
        <div className="absolute inset-0 rounded-md bg-gradient-to-r from-purple-400/20 via-violet-500/20 to-purple-600/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-110" />
      </Button>

      {/* Tooltip Portal - renders outside main container */}
      {showScanTooltip && !scanLoading && buttonRect && ReactDOM.createPortal(
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            position: 'fixed',
            top: buttonRect.bottom + 8,
            left: Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)),
            zIndex: 10000
          }}
        >
          {/* Arrow pointing up - positioned relative to button center */}
          <div 
            className="w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45 absolute -top-1.5 shadow-sm"
            style={{
              left: Math.max(16, Math.min(272, buttonRect.left + (buttonRect.width / 2) - (Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)))))
            }}
          ></div>
          
          {/* Tooltip content */}
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 relative">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                  <Sparkles size={12} className="text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 text-sm">AI-Powered Batch Scanning</h3>
              </div>
              
              <p className="text-gray-600 text-sm leading-relaxed">
                Scan multiple food items at once! AI will recognize and catalog everything visible in your photo.
              </p>
              
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Perfect for scanning:</p>
                <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <span>🧊</span>
                    <span>Entire fridge</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🏠</span>
                    <span>Pantry shelves</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🧾</span>
                    <span>Grocery receipts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>📦</span>
                    <span>Grouped items</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>,
        document.body
      )}
    </>
  );
});

const Inventory = () => {
  const { inventory, loadInventory, isOnline, showScanResults, user, removeItem } = useNoshStore();
  const [location, setLocation] = useState<'all' | 'fridge' | 'pantry' | 'freezer'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    // Initialize from localStorage or default to 'grid'
    const savedViewMode = localStorage.getItem('inventory-view-mode');
    return (savedViewMode === 'list' || savedViewMode === 'grid') ? savedViewMode : 'grid';
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showMobileFilterDropdown, setShowMobileFilterDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'expiry' | 'name'>('expiry');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [selectedItem, setSelectedItem] = useState<FoodItem | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  
  // NEW: Quick Action mode state for mobile
  const [isQuickActionMode, setIsQuickActionMode] = useState(false);
  
  // Usage limit modal now handled globally - removed individual state
  
  // Scroll direction detection for frosted glass effect
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const lastScrollTop = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | null>(null);
  
  // Scan functionality state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  
  // Use global image scanner
  const { isScanning, startScan } = useGlobalImageScanner();
  
  // Handle view mode change with persistence
  const handleViewModeChange = (newViewMode: 'grid' | 'list') => {
    setViewMode(newViewMode);
    localStorage.setItem('inventory-view-mode', newViewMode);
  };

  // Quick delete handler for desktop list view
  const handleQuickDelete = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation(); // Prevent item click modal from opening
    
    try {
      // Delete from database
      const success = await DatabaseService.getInstance().deleteFoodItem(itemId);
      
      if (success) {
        // Remove from frontend store
        removeItem(itemId);
        console.log('✅ Item deleted successfully');
      } else {
        console.error('❌ Failed to delete item from database');
      }
    } catch (error) {
      console.error('❌ Error deleting item:', error);
    }
  };
  
  // Check data freshness on mount - App.tsx now handles initial loading
  useEffect(() => {
    // Inventory mounted
    
    // Only load data if missing (App.tsx handles initial loading)
    if (inventory.length === 0) {
      console.log('🔄 Inventory missing data, triggering load...');
      loadInventory();
    }
  }, []);

  // Secondary effect to handle cases where inventory is empty but user might be authenticated
  useEffect(() => {
    const recheckInventory = async () => {
      // If inventory is empty and we're online, try loading again after a short delay
      if (inventory.length === 0 && isOnline) {
        console.log('⚠️ Inventory empty but online - retrying load...');
        setTimeout(async () => {
          await loadInventory();
        }, 1000); // Wait 1 second then retry
      }
    };

    recheckInventory();
  }, [inventory.length, isOnline, loadInventory]);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Scroll direction detection for frosted glass effect
  useEffect(() => {
    const scrollContainer = document.querySelector('[data-scroll-container]') as HTMLElement;
    scrollContainerRef.current = scrollContainer;
    
    if (!scrollContainer) return;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      const previousScrollTop = lastScrollTop.current;
      
      // Determine scroll direction
      if (currentScrollTop > previousScrollTop && currentScrollTop > 10) {
        // Scrolling down and past initial threshold
        if (scrollDirection.current !== 'down') {
          scrollDirection.current = 'down';
          setIsScrolled(true);
        }
      } else if (currentScrollTop < previousScrollTop) {
        // Scrolling up - immediately return to solid white
        if (scrollDirection.current !== 'up') {
          scrollDirection.current = 'up';
          setIsScrolled(false);
        }
      }
      
      // Update last scroll position
      lastScrollTop.current = currentScrollTop;
      
      // Keep existing mobile scroll Y tracking
      if (isMobile) {
        setScrollY(currentScrollTop);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Handle outside click to close filter dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showFilterDropdown && !target.closest('[data-filter-dropdown]') && !target.closest('[data-filter-button]')) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  // Handler for scan image using global scanner
  const handleScanImage = async (file: File) => {
    console.log('🔍 [Inventory] Starting image scan:', {
      fileName: file.name,
      fileSize: Math.round(file.size / 1024) + 'KB'
    });

    setScanError(null);

    try {
      await startScan(
        file, 
        'inventory',
        // On completion callback
        (result) => {
          console.log('✅ [Inventory] Scan completed:', result);
          
          // Check if upgrade is required - now handled globally
          if (result.requiresUpgrade) {
            console.warn('🚫 [Inventory] Scan blocked by usage limits - global modal will show');
            return;
          }
          
          console.log('📦 [Inventory] Using global scan results modal');
          showScanResults({
            scanData: result.data,
            imageUrl: result.imageUrl || '',
            imagePath: result.imagePath || ''
          });
          
          console.log('🔗 [Inventory] Scan data passed to global modal:', {
            itemCount: result.data?.items?.length || 0,
            imageUrl: result.imageUrl,
            imagePath: result.imagePath
          });
        },
        // On error callback
        (error) => {
          console.error('❌ [Inventory] Scan failed:', error);
          setScanError(error);
        }
      );
    } catch (error: any) {
      console.error('❌ [Inventory] Failed to start scan:', error);
      setScanError(error.message);
    }
  };

  // Trigger file input for desktop
  const triggerFileInput = () => {
    if (!isMobile && fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };



  const filterOptions: FilterOption[] = [
    { id: 'expired', label: 'Expired Items', value: 'expired' },
    { id: 'expiring-soon', label: 'Expiring Soon (3 days)', value: 'expiring-soon' },
    { id: 'fresh', label: 'Fresh Items (7+ days)', value: 'fresh' },
  ];

  const sortOptions: SortOption[] = [
    { id: 'expiry-asc', label: 'Expiration Date (Soon first)', key: 'expiry', direction: 'asc' },
    { id: 'expiry-desc', label: 'Expiration Date (Latest first)', key: 'expiry', direction: 'desc' },
    { id: 'name-asc', label: 'Name (A-Z)', key: 'alphabetical', direction: 'asc' },
    { id: 'name-desc', label: 'Name (Z-A)', key: 'alphabetical', direction: 'desc' },
    { id: 'quantity-desc', label: 'Quantity (High to Low)', key: 'quantity', direction: 'desc' },
    { id: 'quantity-asc', label: 'Quantity (Low to High)', key: 'quantity', direction: 'asc' },
  ];

  // Filter and sort items with stable sorting to prevent flickering during scanning
  const filteredItems = useMemo(() => {
    let items = location === 'all' ? inventory : inventory.filter(item => item.location === location);
    
    // Apply search filter
    if (searchQuery.trim()) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.unit.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply category filter
    if (categoryFilter) {
      items = items.filter(item => item.category === categoryFilter);
    }
    
    // Apply sorting with stable comparison to prevent flickering during AI scanning
    items.sort((a, b) => {
      let valueA: any, valueB: any;
      if (sortBy === 'expiry') {
        valueA = daysUntilExpiry(a.expiryDate);
        valueB = daysUntilExpiry(b.expiryDate);
        
        // If days are equal, use item ID as secondary sort for stability
        if (valueA === valueB) {
          return a.id.localeCompare(b.id);
        }
      } else {
        valueA = a.name.toLowerCase();
        valueB = b.name.toLowerCase();
        
        // If names are equal, use item ID as secondary sort for stability
        if (valueA === valueB) {
          return a.id.localeCompare(b.id);
        }
      }
      
      if (sortDir === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
    
    return items;
  }, [inventory, location, searchQuery, categoryFilter, sortBy, sortDir]);

  const clearSearch = () => {
    setSearchQuery('');
  };

  const toggleFilter = (filterId: string) => {
    setSelectedFilters(prev => 
      prev.includes(filterId) 
        ? prev.filter(id => id !== filterId)
        : [...prev, filterId]
    );
  };

  const clearAllFilters = () => {
    setSelectedFilters([]);
    setSortBy('expiry');
    setSortDir('asc');
    setCategoryFilter('');
  };

  const activeFiltersCount = selectedFilters.length + (sortBy !== 'expiry' ? 1 : 0) + (categoryFilter ? 1 : 0);

  // Floating Filter Modal as Portal
  const floatingFilterModal = isMobile && showFloatingFilter ? ReactDOM.createPortal(
    <div>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-[1000]"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={() => setShowFloatingFilter(false)}
      />
      {/* Modal */}
      <div
        className="fixed top-32 right-6 left-6 z-[1001] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 max-w-xs mx-auto"
        style={{ position: 'fixed', top: '8rem', left: '1.5rem', right: '1.5rem', zIndex: 1001 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Filter & Sort</h3>
          <Button variant="ghost" size="sm" onClick={() => { setShowFloatingFilter(false); setCategoryFilter(''); setSortBy('expiry'); setSortDir('asc'); }} className="text-xs">Clear All</Button>
        </div>
        {/* Sort Section: Two toggles */}
        <div className="mb-4 flex gap-2">
          <button
            onClick={() => {
              if (sortBy === 'expiry') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
              else { setSortBy('expiry'); setSortDir('asc'); }
            }}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${sortBy === 'expiry' ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            Expiration Date
            {sortBy === 'expiry' && (sortDir === 'asc' ? <ArrowUp className="ml-1" size={16} /> : <ArrowDown className="ml-1" size={16} />)}
          </button>
          <button
            onClick={() => {
              if (sortBy === 'name') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
              else { setSortBy('name'); setSortDir('asc'); }
            }}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${sortBy === 'name' ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            Name
            {sortBy === 'name' && (sortDir === 'asc' ? <ArrowUpAZ className="ml-1" size={16} /> : <ArrowDownAZ className="ml-1" size={16} />)}
          </button>
        </div>
        {/* Filter by Category */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Filter by Category</h4>
          <div className="flex flex-wrap gap-2">
            {FOOD_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
                className={`px-3 py-1 rounded-lg text-sm font-medium border transition-colors ${categoryFilter === cat ? 'bg-primary text-white border-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        
        {/* Quick Action Toggle */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Quick Actions</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">Enable quick delete for items</p>
            </div>
            <button
              onClick={() => {
                setIsQuickActionMode(!isQuickActionMode);
                setShowFloatingFilter(false);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${
                isQuickActionMode 
                  ? 'bg-red-500 text-white border-red-500 hover:bg-red-600' 
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {isQuickActionMode ? 'Disable' : 'Enable'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Calculate scroll animation values
  const isSearchSticky = scrollY > 80;

  const handleItemClick = (item: FoodItem) => {
    setSelectedItem(item);
    setShowItemModal(true);
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  // Add mobile swipe back functionality
  useMobileSwipeBack({
    enableSwipeBack: false, // COMPLETELY DISABLED to prevent accidental logout
    threshold: 100,
    edgeWidth: 50
  });

  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'Inventory',
    enabled: true,
    edgeWidth: 50
  });

  return (
    <>
      {/* Mobile Search and Filter Card - Moved to Top */}
      {isMobile && (
        <div className="sticky top-0 z-50 transition-all duration-300">
          <div className="px-2 pt-4">
            <div className="bg-transparent rounded-2xl p-4">
              <div className="flex flex-col gap-3">
                {/* Search Bar with Add Item and Filter Buttons */}
                <div className="flex gap-3 items-center">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Search your inventory..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={cn(
                        "w-full pl-10 pr-10 py-3 rounded-xl text-gray-900 placeholder-gray-400 text-base focus:outline-none transition-all duration-300",
                        isScrolled 
                          ? cn(
                              "thick-glass-effect",
                              user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                            )
                          : "bg-white shadow-sm"
                      )}
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                  {/* Add Item Button */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        const addItemEvent = new CustomEvent('openAddItemModal');
                        window.dispatchEvent(addItemEvent);
                      }}
                      className={cn(
                        "flex items-center justify-center px-3 py-3 rounded-xl transition-all duration-300 bg-red-500 hover:bg-red-600 text-white shadow-sm",
                        isScrolled 
                          ? "bg-red-500/90 hover:bg-red-600/90 backdrop-blur-sm"
                          : ""
                      )}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  {/* Inline Filter Button */}
                  <div className="relative">
                    <button
                      onClick={() => setShowFloatingFilter(true)}
                      className={cn(
                        "flex items-center justify-center px-3 py-3 rounded-xl transition-all duration-300",
                        isScrolled 
                          ? cn(
                              "thick-glass-effect",
                              user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                            )
                          : activeFiltersCount > 0 || isQuickActionMode
                            ? 'bg-primary text-white shadow-sm' 
                            : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
                      )}
                    >
                      <Filter size={18} />
                    </button>
                    {/* Red dot indicator for active filters or quick action mode */}
                    {(activeFiltersCount > 0 || isQuickActionMode) && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                    )}
                  </div>
                </div>
                {/* iOS Segmented Control Tabs */}
                <div className={cn(
                  "flex w-full rounded-xl p-1 gap-1 transition-all duration-300",
                  isScrolled 
                    ? cn(
                        "thick-glass-effect",
                        user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                      )
                    : "bg-white shadow-sm"
                )}>
                  {['all', 'fridge', 'pantry', 'freezer'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setLocation(tab as any)}
                      className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                        location === tab
                          ? 'bg-primary text-white shadow-sm' 
                          : 'bg-transparent text-gray-600 dark:text-gray-300'
                      }`}
                      style={{ transition: 'background 0.2s, color 0.2s' }}
                    >
                      {tab === 'all' && <span>📦 All</span>}
                      {tab === 'fridge' && <span>🧊 Fridge</span>}
                      {tab === 'pantry' && <span>🏠 Pantry</span>}
                      {tab === 'freezer' && <span>❄️ Freezer</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`w-full h-full ${isMobile ? 'min-h-screen px-0' : 'max-w-6xl mx-auto'}`}>
        {/* Desktop Header - only show on desktop */}
        {!isMobile && (
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-heading font-bold text-foreground">Food Inventory</h1>
            <div className="flex gap-2">
              {/* <ThemeToggle /> */}
              {/* Fancy AI Scan Button with Tooltip */}
              <ScanButtonWithTooltip
                triggerFileInput={triggerFileInput}
                scanLoading={isScanning}
              />
              <Button onClick={() => {
                // Dispatch custom event that Layout can listen to
                const addItemEvent = new CustomEvent('openAddItemModal');
                window.dispatchEvent(addItemEvent);
              }}>
                <Plus size={18} className="mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        )}

        {/* Click outside to close filter dropdown */}
        {showMobileFilterDropdown && (
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowMobileFilterDropdown(false)}
          />
        )}



        {/* Inventory Content Container - Separate from Sticky Header */}
        <div className={`w-full ${isMobile ? 'space-y-0' : 'space-y-0'} ${isMobile ? 'px-0' : ''} ${isMobile ? 'max-w-full' : ''}`}>
          
          {/* Desktop Search and Filter Section - Make this sticky */}
          {!isMobile && (
            <div className="sticky top-0 z-40">
              <div className={cn(
                "rounded-xl shadow-soft p-4 transition-all duration-300",
                isScrolled 
                  ? cn(
                      "thick-glass-effect",
                      user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                    )
                  : "bg-white"
              )}>
                {/* Filter Tabs */}
                <Tabs value={location} onValueChange={(value) => setLocation(value as any)}>
                  <TabsList className={cn(
                    "w-full mb-4 grid grid-cols-4 transition-all duration-300",
                    isScrolled 
                      ? "bg-white/20 backdrop-blur-sm" 
                      : "bg-gray-50 dark:bg-gray-700"
                  )}>
                    <TabsTrigger value="all" className="flex-1 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:dark:bg-gray-600 text-gray-600 dark:text-gray-300 data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100">
                      <span className="mr-1">📦</span>
                      <span>All</span>
                    </TabsTrigger>
                    <TabsTrigger value="fridge" className="flex-1 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:dark:bg-gray-600 text-gray-600 dark:text-gray-300 data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100">
                      <span className="mr-1">🧊</span>
                      <span>Fridge</span>
                    </TabsTrigger>
                    <TabsTrigger value="pantry" className="flex-1 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:dark:bg-gray-600 text-gray-600 dark:text-gray-300 data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100">
                      <span className="mr-1">🏠</span>
                      <span>Pantry</span>
                    </TabsTrigger>
                    <TabsTrigger value="freezer" className="flex-1 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:dark:bg-gray-600 text-gray-600 dark:text-gray-300 data-[state=active]:text-gray-900 data-[state=active]:dark:text-gray-100">
                      <span className="mr-1">❄️</span>
                      <span>Freezer</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Desktop View Controls - Single Row Layout */}
                  <div className="flex items-center gap-4 mb-4">
                    {/* Items Count */}
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                      {filteredItems.length} items
                      {searchQuery && (
                        <span className="ml-1 text-primary">
                          for "{searchQuery}"
                        </span>
                      )}
                      {activeFiltersCount > 0 && (
                        <span className="ml-1 text-primary">
                          (filtered)
                        </span>
                      )}
                    </div>
                    
                    {/* Search Bar */}
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
                      <input
                        type="text"
                        placeholder="Search your inventory..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={cn(
                          "w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300",
                          isScrolled 
                            ? "bg-white/20 backdrop-blur-sm border-white/30" 
                            : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                        )}
                      />
                      {searchQuery && (
                        <button
                          onClick={clearSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                    
                    {/* Filter and View Controls */}
                    <div className="flex gap-2">
                      <div className="relative">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                          className={cn(
                            "hover:bg-accent transition-all duration-300",
                            isScrolled 
                              ? "bg-white/20 backdrop-blur-sm" 
                              : "bg-gray-50 dark:bg-gray-700",
                            activeFiltersCount > 0 ? 'text-primary' : ''
                          )}
                          data-filter-button
                        >
                          <Filter size={16} />
                        </Button>
                        {/* Red dot indicator for active filters */}
                        {activeFiltersCount > 0 && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                        )}
                        
                        {/* Desktop Filter Dropdown */}
                        {showFilterDropdown && (
                          <div 
                            className="absolute right-0 top-12 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4"
                            style={{ zIndex: 9999 }}
                            data-filter-dropdown
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Filter & Sort</h3>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => { 
                                  setShowFilterDropdown(false); 
                                  setCategoryFilter(''); 
                                  setSortBy('expiry'); 
                                  setSortDir('asc'); 
                                }} 
                                className="text-xs"
                              >
                                Clear All
                              </Button>
                            </div>
                            {/* Sort Section: Two toggles */}
                            <div className="mb-4 flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (sortBy === 'expiry') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                  else { setSortBy('expiry'); setSortDir('asc'); }
                                }}
                                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${sortBy === 'expiry' ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                              >
                                Expiration Date
                                {sortBy === 'expiry' && (sortDir === 'asc' ? <ArrowUp className="ml-1" size={16} /> : <ArrowDown className="ml-1" size={16} />)}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (sortBy === 'name') setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                  else { setSortBy('name'); setSortDir('asc'); }
                                }}
                                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${sortBy === 'name' ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                              >
                                Name
                                {sortBy === 'name' && (sortDir === 'asc' ? <ArrowUpAZ className="ml-1" size={16} /> : <ArrowDownAZ className="ml-1" size={16} />)}
                              </button>
                            </div>
                            {/* Filter by Category */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Filter by Category</h4>
                              <div className="flex flex-wrap gap-2">
                                {FOOD_CATEGORIES.map(cat => (
                                  <button
                                    key={cat}
                                    type="button"
                                    onClick={() => {
                                      console.log('Category clicked:', cat);
                                      setCategoryFilter(cat === categoryFilter ? '' : cat);
                                    }}
                                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors cursor-pointer ${categoryFilter === cat ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                                  >
                                    {cat}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleViewModeChange(viewMode === 'grid' ? 'list' : 'grid')}
                        className={cn(
                          "hover:bg-accent transition-all duration-300",
                          isScrolled 
                            ? "bg-white/20 backdrop-blur-sm" 
                            : "bg-gray-50 dark:bg-gray-700"
                        )}
                      >
                        {viewMode === 'grid' ? <List size={16} /> : <Grid size={16} />}
                        <span className="ml-2">{viewMode === 'grid' ? 'List' : 'Grid'}</span>
                      </Button>
                    </div>
                  </div>
                </Tabs>
              </div>
            </div>
          )}

          {/* Items Content - Light gray background to match main page */}
          <div className={`w-full ${isMobile ? '' : 'bg-background min-h-screen pt-8'}`}>
            <Tabs value={location} onValueChange={(value) => setLocation(value as any)}>
              <TabsContent value={location} className={`w-full space-y-4 ${isMobile ? 'px-0 pb-40' : 'px-0 pb-32'}`}>
                {/* Mobile Individual Cards - No Outer Container */}
                {isMobile ? (
                  <>
                    {isQuickActionMode ? (
                      // Quick Action Mode: Single column with delete buttons
                      <div className="w-full space-y-2 px-1 sm:space-y-3 sm:px-0">
                        {filteredItems.map(item => (
                          <div key={item.id} className="bg-white rounded-2xl shadow-sm">
                            <MobileQuickActionListItem item={item} onQuickDelete={handleQuickDelete} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Normal Mode: Grid or List view
                      <>
                        {viewMode === 'grid' ? (
                          <div className="w-full grid grid-cols-2 gap-2 px-1 sm:gap-3 sm:px-0 sm:grid-cols-[repeat(auto-fit,minmax(215px,1fr))]">
                            {filteredItems.map(item => (
                              <div key={item.id} className="bg-white rounded-2xl shadow-sm" onClick={() => handleItemClick(item)}>
                                <InventoryGridItem item={item} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="w-full space-y-3 px-1 sm:space-y-3 sm:px-0">
                            {filteredItems.map(item => (
                              <div key={item.id} className="bg-white rounded-2xl shadow-sm" onClick={() => handleItemClick(item)}>
                                <InventoryListItem item={item} />
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    
                    {filteredItems.length === 0 && (
                      <div className="bg-white rounded-2xl shadow-sm p-6 mx-1 sm:mx-0">
                        <div className="text-center py-12">
                          <div className="text-4xl mb-4">📦</div>
                          <h3 className="font-medium text-foreground mb-2">
                            {searchQuery || activeFiltersCount > 0 
                              ? `No items found` 
                              : `No items ${location === 'all' ? 'found' : `in your ${location}`}`
                            }
                          </h3>
                          <p className="text-muted-foreground text-sm mb-4">
                            {searchQuery || activeFiltersCount > 0
                              ? "Try adjusting your search terms or filters" 
                              : "Scan multiple items with AI using the Scan Item button below, or add single items with the + button above"
                            }
                          </p>
                          {searchQuery || activeFiltersCount > 0 ? (
                            <div className="flex gap-2 justify-center">
                              {searchQuery && (
                                <Button onClick={clearSearch} variant="outline">
                                  Clear Search
                                </Button>
                              )}
                              {activeFiltersCount > 0 && (
                                <Button onClick={clearAllFilters} variant="outline">
                                  Clear Filters
                                </Button>
                              )}
                            </div>
                          ) : (
                            isMobile ? (
                              // Mobile: Show visual reference to floating nav plus button
                              <div className="flex flex-col items-center gap-3">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>Tap the red</span>
                                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                                    <Plus size={16} className="text-white" strokeWidth={3} />
                                  </div>
                                  <span>button below to get started</span>
                                </div>
                              </div>
                            ) : (
                              <ScanButtonWithTooltip 
                                triggerFileInput={triggerFileInput}
                                scanLoading={isScanning}
                              />
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Desktop Content - No white container, just items on gray background */
                  <>
                    {viewMode === 'grid' ? (
                      <div className="w-full grid grid-cols-2 gap-2 sm:gap-3 sm:grid-cols-[repeat(auto-fit,minmax(215px,1fr))]">
                        {filteredItems.map(item => (
                          <div key={item.id} onClick={() => handleItemClick(item)}>
                            <InventoryGridItem item={item} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="w-full space-y-2 max-w-full">
                        {filteredItems.map(item => (
                          <div key={item.id} onClick={() => handleItemClick(item)}>
                            <InventoryListItem item={item} onQuickDelete={handleQuickDelete} />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {filteredItems.length === 0 && (
                      <div className="text-center py-12">
                        <div className="text-4xl mb-4">📦</div>
                        <h3 className="font-medium text-foreground mb-2">
                          {searchQuery || activeFiltersCount > 0 
                            ? `No items found` 
                            : `No items ${location === 'all' ? 'found' : `in your ${location}`}`
                          }
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4">
                          {searchQuery || activeFiltersCount > 0
                            ? "Try adjusting your search terms or filters" 
                            : "Scan multiple items with AI using the button below, or add single items with the + button above"
                          }
                        </p>
                        {searchQuery || activeFiltersCount > 0 ? (
                          <div className="flex gap-2 justify-center">
                            {searchQuery && (
                              <Button onClick={clearSearch} variant="outline">
                                Clear Search
                              </Button>
                            )}
                            {activeFiltersCount > 0 && (
                              <Button onClick={clearAllFilters} variant="outline">
                                Clear Filters
                              </Button>
                            )}
                          </div>
                        ) : (
                          !isMobile && (
                            <ScanButtonWithTooltip 
                              triggerFileInput={triggerFileInput}
                              scanLoading={isScanning}
                            />
                          )
                        )}
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
          
          {floatingFilterModal}
        </div>

        {/* Item Details Modal */}
        <ItemDetailsModal
          item={selectedItem}
          isOpen={showItemModal}
          onClose={closeItemModal}
        />

        {/* Hidden file input for desktop scanning */}
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              console.log('🖥️ [Desktop] File selected for inventory scan:', {
                name: file.name,
                size: Math.round(file.size / 1024) + 'KB',
                type: file.type
              });
              handleScanImage(file);
            }
          }}
        />

        {/* Usage Limit Modal now handled globally in App.tsx */}

      </div>
    </>
  );
};

const InventoryGridItem = ({ item }: { item: FoodItem }) => {
  const days = daysUntilExpiry(item.expiryDate);
  const cardBg = getExpiryBackgroundColor(days, item.location);
  const borderColor = LOCATION_BORDER_MAP[item.location] || LOCATION_BORDER_MAP.default;

  return (
    <motion.div
      className={`w-full sm:min-w-[215px] ${cardBg} ${borderColor} rounded-2xl overflow-hidden transition-all duration-200 flex items-center shadow-soft hover:shadow-lg cursor-pointer relative min-h-[90px] md:min-h-[130px] px-3 py-2 md:px-4 md:py-4`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Emoji icon on the left, larger, fixed width */}
      <span className="text-2xl md:text-3xl w-8 md:w-10 flex-shrink-0 text-center">{item.emoji}</span>
      {/* Left-aligned content, closer to emoji, responsive */}
      <div className="flex-1 flex flex-col items-start justify-center min-w-0 ml-2 md:ml-3">
        <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-0.5 truncate w-full leading-tight">{item.name}</h3>
        <div className="text-xs text-gray-600 font-light truncate w-full mb-1">{item.quantity} {item.unit}</div>
        {/* Expiry tag: below quantity on mobile, bottom right on desktop */}
        <div className="block md:hidden">
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)}`}>
            {getExpiryDisplayText(days, item.location)}
          </span>
        </div>
      </div>
      {/* Expiry tag: bottom right on desktop */}
      <div className={`hidden md:block absolute bottom-3 right-3 rounded-full px-3 py-1 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)}`}> 
        {getExpiryDisplayText(days, item.location)}
      </div>
    </motion.div>
  );
};

const InventoryListItem = ({ item, onQuickDelete }: { item: FoodItem; onQuickDelete?: (e: React.MouseEvent, itemId: string) => void }) => {
  const days = daysUntilExpiry(item.expiryDate);
  const cardBg = getExpiryBackgroundColor(days, item.location);
  const borderColor = LOCATION_BORDER_MAP[item.location] || LOCATION_BORDER_MAP.default;

  return (
    <motion.div
      className={`w-full sm:min-w-[215px] ${cardBg} ${borderColor} rounded-xl overflow-hidden transition-colors flex items-center shadow-soft hover:shadow-lg cursor-pointer relative min-h-[70px] md:min-h-[100px] px-3 py-2 md:px-4 md:py-4`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Emoji icon on the left, larger, fixed width */}
      <span className="text-2xl md:text-3xl w-8 md:w-10 flex-shrink-0 text-center">{item.emoji}</span>
      {/* Left-aligned content, closer to emoji, responsive */}
      <div className="flex-1 flex flex-col items-start justify-center min-w-0 ml-2 md:ml-3">
        <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-0.5 truncate w-full leading-tight">{item.name}</h3>
        <div className="text-xs text-gray-600 font-light truncate w-full mb-1">{item.quantity} {item.unit}</div>
        {/* Expiry tag: below quantity on mobile, bottom right on desktop */}
        <div className="block md:hidden">
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)}`}>
            {getExpiryDisplayText(days, item.location)}
          </span>
        </div>
      </div>
      
      {/* Desktop expiry tag and quick delete button */}
      <div className="hidden md:flex items-center gap-3">
        {/* Expiry tag */}
        <div className={`rounded-full px-3 py-1 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)}`}> 
          {getExpiryDisplayText(days, item.location)}
        </div>
        
        {/* Quick Delete Button */}
        {onQuickDelete && (
          <button
            onClick={(e) => onQuickDelete(e, item.id)}
            className="flex-shrink-0 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium text-sm transition-all duration-200 hover:scale-105 active:scale-95"
            title="Delete item"
          >
            Quick Delete
          </button>
        )}
      </div>
    </motion.div>
  );
};

// NEW: Mobile Quick Action List Item Component
const MobileQuickActionListItem = ({ item, onQuickDelete }: { item: FoodItem; onQuickDelete: (e: React.MouseEvent, itemId: string) => void }) => {
  const days = daysUntilExpiry(item.expiryDate);
  const cardBg = getExpiryBackgroundColor(days, item.location);
  const borderColor = LOCATION_BORDER_MAP[item.location] || LOCATION_BORDER_MAP.default;

  return (
    <motion.div
      className={`w-full ${cardBg} ${borderColor} rounded-xl overflow-hidden transition-colors flex items-center shadow-soft relative min-h-[70px] px-3 py-2`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Emoji icon on the left */}
      <span className="text-2xl w-8 flex-shrink-0 text-center">{item.emoji}</span>
      
      {/* Content in middle */}
      <div className="flex-1 flex flex-col items-start justify-center min-w-0 ml-3">
        <h3 className="font-semibold text-sm text-gray-900 mb-0.5 truncate w-full leading-tight">{item.name}</h3>
        <div className="text-xs text-gray-600 font-light truncate w-full mb-1">{item.quantity} {item.unit}</div>
        <div>
          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)}`}>
            {getExpiryDisplayText(days, item.location)}
          </span>
        </div>
      </div>
      
      {/* Quick Delete Button on the right */}
      <button
        onClick={(e) => onQuickDelete(e, item.id)}
        className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all duration-200 hover:scale-105 active:scale-95 flex items-center justify-center"
        title="Delete item"
      >
        <Trash2 size={18} />
      </button>
    </motion.div>
  );
};

export default Inventory;
